import { useEffect } from 'react';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  AntdEmpty,
  AntdGrid,
  AntdInput,
  AntdModal,
  AntdSpin,
  AntdTypography,
  Button,
} from '@ui/components';
import { useDiseasesSearch } from './hooks';

export type DiseaseOptionV2Legacy = {
  icdCode: string;
  icdId: string;
  label: string;
  trialCount: number;
  value: string;
};

const { Text } = AntdTypography;
const { useBreakpoint } = AntdGrid;

type DiseaseSearchProps = {
  autoFocus?: boolean;
  clearable?: boolean;
  enabledAnalytics?: boolean;
  fakeInputId?: string;
  fullScreen?: boolean;
  includeHealthy?: boolean;
  inputId?: string;
  isMultiple?: boolean;
  onChange?: (disease?: DiseaseOptionV2Legacy | DiseaseOptionV2Legacy[]) => void;
  queryOnSearch?: boolean;
  value?: DiseaseOptionV2Legacy | DiseaseOptionV2Legacy[];
  valueProperty?: keyof DiseaseOptionV2Legacy;
};

export const DiseaseSearch = ({
  onChange,
  value: selectedDisease,
  fakeInputId,
  inputId,
  clearable,
  fullScreen = false,
  autoFocus = false,
  queryOnSearch = false,
  isMultiple = false,
  includeHealthy = false,
  enabledAnalytics = false,
  valueProperty = 'label',
}: DiseaseSearchProps) => {
  const { t } = useTranslation();
  const { md } = useBreakpoint();
  const {
    count,
    inputRef,
    isLoading,
    hasNextPage,
    searchString,
    diseaseOptions,
    healthyDiseaseOption,
    isDiseasesSearchOpen,
    fetchNextPage,
    handleSearchChange,
    toggleDiseasesSearch,
    trackDiseaseSearchDiseaseSelected,
  } = useDiseasesSearch(queryOnSearch, enabledAnalytics);

  const getIsDiseaseSelected = (disease: DiseaseOptionV2Legacy) => {
    if (isMultiple) {
      return (selectedDisease as DiseaseOptionV2Legacy[])?.some(
        (d) => d[valueProperty] === disease[valueProperty],
      );
    }
    return (
      disease.label === (selectedDisease as DiseaseOptionV2Legacy)?.label &&
      disease.value === (selectedDisease as DiseaseOptionV2Legacy)?.value
    );
  };

  const handleSelect = (disease: DiseaseOptionV2Legacy | DiseaseOptionV2Legacy[]) => {
    if (isMultiple) {
      onChange?.(
        getIsDiseaseSelected(disease as DiseaseOptionV2Legacy)
          ? (selectedDisease as DiseaseOptionV2Legacy[])?.filter(
              (d) => d[valueProperty] !== (disease as DiseaseOptionV2Legacy)[valueProperty],
            )
          : [
              ...((selectedDisease as DiseaseOptionV2Legacy[]) || []),
              disease as DiseaseOptionV2Legacy,
            ],
      );
    } else {
      const isDiseaseSelected = getIsDiseaseSelected(disease as DiseaseOptionV2Legacy);
      onChange?.(isDiseaseSelected ? undefined : disease);

      if (!isDiseaseSelected) {
        trackDiseaseSearchDiseaseSelected();
      }
    }
    toggleDiseasesSearch();
  };

  useEffect(() => {
    const focusInput = () => {
      if (autoFocus && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    };

    if (isDiseasesSearchOpen) {
      focusInput();
    }
  }, [autoFocus, isDiseasesSearchOpen]);

  return (
    <>
      <Button
        size="large"
        id={fakeInputId}
        aria-label={fakeInputId}
        onClick={toggleDiseasesSearch}
        className={cx(
          'w-full h-10 flex items-center justify-between px-3',
          clearable && 'w-11/12',
          autoFocus && 'border-base-600',
        )}
      >
        <Text className="text-base overflow-hidden">
          {isMultiple
            ? (selectedDisease as DiseaseOptionV2Legacy[])?.map((d) => d[valueProperty]).join(', ')
            : (selectedDisease as DiseaseOptionV2Legacy)?.[valueProperty] || ''}
        </Text>
        <SearchOutlined className="text-base-500" />
      </Button>
      <AntdModal
        title={
          md && (
            <Text className="text-2xl font-semibold">
              {t('sign_up_v2_page.disease_form.search.title')}
            </Text>
          )
        }
        closeIcon={<CloseOutlined className="mt-3 md:mt-0" />}
        open={isDiseasesSearchOpen}
        onCancel={toggleDiseasesSearch}
        footer={null}
        width={md && !fullScreen ? '90vw' : '100vw'}
        className={cx(
          'md:max-w-screen-2xl',
          !md ? '!top-0 !p-0 !m-0 !w-full !h-full !max-w-none' : '',
          fullScreen && '!top-0 !p-0 !m-0 !w-full !h-full !max-w-none',
        )}
        classNames={{
          body: cx('h-screen md:h-auto min-h-[400px]', fullScreen && 'h-screen'),
          container: cx(
            'h-screen md:h-auto rounded-none md:rounded-2xl p-0',
            fullScreen && 'rounded-none !rounded-none min-h-screen',
          ),
          wrapper: cx('h-screen md:h-auto overflow-hidden', fullScreen && 'h-screen'),
          header: 'p-6 pb-0',
        }}
      >
        <div className="py-4 px-4 md:px-6 w-11/12 md:w-full">
          <AntdInput
            placeholder={t('sign_up_v2_page.disease_form.search.search_placeholder')}
            size="large"
            id={inputId}
            aria-label={inputId}
            suffix={<SearchOutlined className="text-base-500" />}
            value={searchString}
            ref={inputRef}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        {!diseaseOptions.length && !isLoading ? (
          <AntdEmpty
            className={'p-6'}
            description={queryOnSearch && !searchString ? t('embedded_v2.start_search') : undefined}
          />
        ) : (
          <InfiniteScroll
            dataLength={count}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={null}
            height={md && !fullScreen ? 'calc(100dvh - 300px)' : 'calc(100dvh - 100px)'}
            className="px-4 md:px-6"
          >
            <ul className="list-none p-0 pb-6">
              {includeHealthy && healthyDiseaseOption && (
                <li key="healthy-disease" className="mt-4 first:mt-0">
                  <Text className="font-medium md:font-semibold text-xs md:text-base text-base-500">
                    {t('sign_up_v2_page.disease_form.search.healthy_disease')}
                  </Text>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Button
                      onClick={() => handleSelect(healthyDiseaseOption)}
                      key="healthy-disease-button"
                      data-testid="healthy-disease-button"
                      className={cx(
                        'px-2 py-1 md:py-2 md:px-4 whitespace-normal rounded-full bg-base-100 transition-all duration-300  hover:scale-105 hover:shadow-md hover:z-10 h-auto text-xs md:text-base',
                        getIsDiseaseSelected(healthyDiseaseOption) && 'bg-primary-600 text-white',
                      )}
                    >
                      {healthyDiseaseOption.label}
                    </Button>
                  </div>
                </li>
              )}
              {diseaseOptions.map(({ id, name, diseases }) => (
                <li key={`category-${id}`} className="mt-4 first:mt-0">
                  <Text className="font-medium md:font-semibold text-xs md:text-base text-base-500">
                    {name}
                  </Text>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {diseases.map((disease, index) => (
                      <Button
                        onClick={() => handleSelect(disease)}
                        key={`disease-${id}-${disease.value}-${index}`}
                        data-testid={`disease-${index + 1}-${inputId}`}
                        className={cx(
                          'px-2 py-1 md:py-2 md:px-4 whitespace-normal rounded-full bg-base-100 transition-all duration-300  hover:scale-105 hover:shadow-md hover:z-10 h-auto text-xs md:text-base',
                          getIsDiseaseSelected(disease) && 'bg-primary-600 text-white',
                        )}
                      >
                        {disease.label}
                      </Button>
                    ))}
                  </div>
                </li>
              ))}
              {isLoading && <AntdSpin size="small" className="block my-3 mx-auto" />}
            </ul>
          </InfiniteScroll>
        )}
      </AntdModal>
    </>
  );
};
